import type { ReactNode } from "react";

/**
 * A bandeira do país de um jogador.
 *
 * **Desenhada aqui, e não emoji.** `🇧🇷` é um par de indicadores regionais, e o
 * Windows não desenha bandeira nenhuma: ele mostra as duas letras em caixinhas.
 * Como o jogo sai para Windows antes de qualquer outra coisa, emoji entregaria
 * a pior versão justamente na plataforma principal.
 *
 * **E também não é pacote de bandeiras.** O gerador tem dez nacionalidades, e
 * um pacote com duzentas e cinquenta traria duzentas e quarenta que ninguém
 * usa. Dez desenhos de três a cinco retângulos cabem num arquivo.
 *
 * As bandeiras são simplificadas de propósito: a esfera do Brasil, o sol da
 * Argentina e o brasão de Portugal viram um círculo, um ponto e um disco. Num
 * alvo de treze pixels nenhum detalhe deles sobrevive, e o que identifica o
 * país nesse tamanho é a combinação de cores e a divisão do pano.
 *
 * Código que não está na lista cai na sigla, que é o que a tela mostrava antes:
 * datapack de terceiro pode trazer qualquer nacionalidade.
 */

/** Proporção 3 por 2, que é a de bandeira de mesa e a que menos distorce. */
const LARGURA = 30;
const ALTURA = 20;

type Desenho = (id: string) => ReactNode;

/** Faixas horizontais de cima para baixo, em partes iguais. */
function horizontais(...cores: string[]): Desenho {
  return () => (
    <>
      {cores.map((cor, i) => (
        <rect
          key={cor + String(i)}
          x={0}
          y={(ALTURA / cores.length) * i}
          width={LARGURA}
          height={ALTURA / cores.length}
          fill={cor}
        />
      ))}
    </>
  );
}

/** Faixas verticais da esquerda para a direita, em partes iguais. */
function verticais(...cores: string[]): Desenho {
  return () => (
    <>
      {cores.map((cor, i) => (
        <rect
          key={cor + String(i)}
          x={(LARGURA / cores.length) * i}
          y={0}
          width={LARGURA / cores.length}
          height={ALTURA}
          fill={cor}
        />
      ))}
    </>
  );
}

const BANDEIRAS: Record<string, Desenho> = {
  BR: () => (
    <>
      <rect width={LARGURA} height={ALTURA} fill="#009b3a" />
      <path d="M15 2.5 27.5 10 15 17.5 2.5 10Z" fill="#fedf00" />
      <circle cx={15} cy={10} r={4.2} fill="#002776" />
    </>
  ),
  AR: () => (
    <>
      {horizontais("#74acdf", "#ffffff", "#74acdf")("AR")}
      <circle cx={15} cy={10} r={2.6} fill="#f6b40e" />
    </>
  ),
  ES: () => (
    <>
      <rect width={LARGURA} height={ALTURA} fill="#aa151b" />
      <rect y={5} width={LARGURA} height={10} fill="#f1bf00" />
    </>
  ),
  EN: () => (
    <>
      <rect width={LARGURA} height={ALTURA} fill="#ffffff" />
      <rect x={12} width={6} height={ALTURA} fill="#ce1124" />
      <rect y={7} width={LARGURA} height={6} fill="#ce1124" />
    </>
  ),
  IT: verticais("#008c45", "#f4f5f0", "#cd212a"),
  PT: () => (
    <>
      <rect width={LARGURA} height={ALTURA} fill="#da291c" />
      <rect width={12} height={ALTURA} fill="#046a38" />
      <circle cx={12} cy={10} r={4} fill="#ffe900" />
      <circle cx={12} cy={10} r={2.4} fill="#da291c" />
    </>
  ),
  UY: () => (
    <>
      <rect width={LARGURA} height={ALTURA} fill="#ffffff" />
      <rect y={4} width={LARGURA} height={2.3} fill="#0038a8" />
      <rect y={8.6} width={LARGURA} height={2.3} fill="#0038a8" />
      <rect y={13.2} width={LARGURA} height={2.3} fill="#0038a8" />
      <rect width={12} height={9} fill="#ffffff" />
      <circle cx={6} cy={4.5} r={2.6} fill="#f6b40e" />
    </>
  ),
  CO: () => (
    <>
      <rect width={LARGURA} height={ALTURA} fill="#fcd116" />
      <rect y={10} width={LARGURA} height={5} fill="#003893" />
      <rect y={15} width={LARGURA} height={5} fill="#ce1126" />
    </>
  ),
  FR: verticais("#002395", "#ffffff", "#ed2939"),
  DE: horizontais("#000000", "#dd0000", "#ffce00"),
};

export function Bandeira({
  codigo,
  nome,
  size = 13,
}: {
  codigo: string;
  /** O país por extenso, para quem ouve a tela em vez de vê-la. */
  nome?: string | undefined;
  size?: number;
}): ReactNode {
  const desenho = BANDEIRAS[codigo.toUpperCase()];

  if (!desenho) {
    return (
      <span className="num" style={{ fontSize: 10.5, color: "var(--text-dim)" }}>
        {codigo}
      </span>
    );
  }

  return (
    <svg
      width={(size * LARGURA) / ALTURA}
      height={size}
      viewBox={`0 0 ${LARGURA} ${ALTURA}`}
      /* Sem título, quem ouve a tela não saberia de que país é o pano. */
      role="img"
      aria-label={nome ?? codigo}
      style={{ flexShrink: 0, borderRadius: 2, display: "block" }}
    >
      {desenho(codigo)}
      {/* Contorno de dentro: a Inglaterra e o Uruguai são quase todos brancos,
          e sem ele a bandeira some no fundo claro do tema claro. */}
      <rect
        x={0.4}
        y={0.4}
        width={LARGURA - 0.8}
        height={ALTURA - 0.8}
        rx={1.4}
        fill="none"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth={0.8}
      />
    </svg>
  );
}
